import { useConfirmModal } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useCallback } from 'react';

import {
  type AppResourceLifecycleImpact,
  AppResourceLifecycleImpactWarning,
} from './app-resource-lifecycle';

export interface AppDataChangeCopy {
  confirm: string;
  description: string;
  title: string;
}

const APP_DATA_CHANGE_CANCELLED = 'APP_DATA_CHANGE_CANCELLED';

// The confirm modal is a single app-wide instance. Several changes ask one
// question after the other instead of replacing an open question.
let pendingConfirmation: Promise<unknown> = Promise.resolve();

/** True for the rejection of a change the user cancelled in the warning. */
export function isAppDataChangeCancelled(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === APP_DATA_CHANGE_CANCELLED
  );
}

/**
 * Asks before a change that affects data other apps read. Resolves without a
 * question when none of the impacts concerns app data, resolves after the
 * user confirms, and rejects with an `isAppDataChangeCancelled` error when the
 * user cancels.
 */
export function useConfirmAppDataChange(): (
  impacts: readonly AppResourceLifecycleImpact[],
  copy: AppDataChangeCopy
) => Promise<void> {
  const { openConfirmModal, closeConfirmModal } = useConfirmModal();
  const t = useI18n();

  return useCallback(
    async (impacts, copy) => {
      const dataImpacts = impacts.filter(
        impact => impact.impact === 'data_source_used_by_app'
      );
      if (dataImpacts.length === 0) {
        return;
      }
      const ask = () =>
        new Promise<boolean>(resolve => {
          let settled = false;
          const settle = (value: boolean) => {
            if (settled) return;
            settled = true;
            // Close before answering, so a queued question cannot be closed
            // by the modal's own deferred auto-close.
            closeConfirmModal();
            resolve(value);
          };
          openConfirmModal(
            {
              title: copy.title,
              description: copy.description,
              children: (
                <AppResourceLifecycleImpactWarning impacts={dataImpacts} />
              ),
              confirmText: copy.confirm,
              cancelText: t['Cancel'](),
              confirmButtonOptions: { variant: 'error' },
              onConfirm: () => settle(true),
              onCancel: () => settle(false),
              onOpenChange: open => {
                if (!open) settle(false);
              },
            },
            { autoClose: false }
          );
        });
      const question = pendingConfirmation.then(ask);
      pendingConfirmation = question.catch(() => undefined);
      if (!(await question)) {
        throw Object.assign(new Error('App data change cancelled'), {
          code: APP_DATA_CHANGE_CANCELLED,
        });
      }
    },
    [closeConfirmModal, openConfirmModal, t]
  );
}
